/**
 * Assignment History Component
 *
 * Modal displaying the full history of SLA tier assignments for a specific client.
 * Shows timeline of past and current assignments.
 *
 * @module components/SLA/AssignmentHistory
 */

import React, { useState, useEffect } from 'react';
import { ClientSLAAssignmentWithTier } from '@shared/types/sla';
import slaService from '../../services/slaService';
import { Modal, Button } from '../ui';
import { formatEastern } from '@/utils/dateFormatter';

interface AssignmentHistoryProps {
  /** Client ID to show history for */
  clientId: string;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * Assignment History - Display client SLA assignment timeline
 */
const AssignmentHistory: React.FC<AssignmentHistoryProps> = ({ clientId, onClose }) => {
  const [history, setHistory] = useState<ClientSLAAssignmentWithTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [clientId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getAssignmentHistory(clientId);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load assignment history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignment history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return formatEastern(date);
  };

  const isActive = (assignment: ClientSLAAssignmentWithTier) => {
    return assignment.expiration_date === null;
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size='lg'
      title='SLA Assignment History'
      footer={
        <Button variant='outline' onClick={onClose}>
          Close
        </Button>
      }
    >
      <p className='text-sm text-[var(--color-text-secondary)]'>
        Complete history of SLA tier assignments for this client
      </p>

      {error && (
        <div className='mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4'>
          <div className='text-sm text-red-800 dark:text-red-300'>{error}</div>
        </div>
      )}

      {loading ? (
        <div className='mt-6 text-center text-[var(--color-text-secondary)]'>Loading history...</div>
      ) : history.length === 0 ? (
        <div className='mt-6 text-center text-[var(--color-text-secondary)]'>No assignment history found</div>
      ) : (
        <div className='mt-6'>
          {/* Timeline */}
          <div className='flow-root'>
            <ul className='-mb-8'>
              {history.map((assignment, idx) => (
                <li key={assignment.id}>
                  <div className='relative pb-8'>
                    {/* Connector line */}
                    {idx !== history.length - 1 && (
                      <span
                        className='absolute top-4 left-4 -ml-px h-full w-0.5 bg-[var(--color-border)]'
                        aria-hidden='true'
                      />
                    )}

                    <div className='relative flex space-x-3'>
                      {/* Icon */}
                      <div>
                        <span
                          className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-[var(--color-bg-card)] ${
                            isActive(assignment) ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        >
                          <svg
                            className='h-5 w-5 text-white'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            {isActive(assignment) ? (
                              <path
                                fillRule='evenodd'
                                d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                                clipRule='evenodd'
                              />
                            ) : (
                              <path
                                fillRule='evenodd'
                                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                                clipRule='evenodd'
                              />
                            )}
                          </svg>
                        </span>
                      </div>

                      {/* Content */}
                      <div className='flex min-w-0 flex-1 justify-between space-x-4 pt-1.5'>
                        <div className='flex-1'>
                          <p className='text-sm text-[var(--color-text-primary)] font-medium'>
                            {assignment.tier_name}
                            {isActive(assignment) && (
                              <span className='ml-2 inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-300'>
                                Active
                              </span>
                            )}
                          </p>
                          <div className='mt-1 text-sm text-[var(--color-text-secondary)] space-y-1'>
                            <div>Uptime: {assignment.effective_uptime_percent}%</div>
                            <div>
                              RTO: {assignment.effective_rto_hours}h, RPO:{' '}
                              {assignment.effective_rpo_hours}h
                            </div>
                            <div>
                              Penalty: ${assignment.effective_penalty_per_hour_usd}/hour
                            </div>
                          </div>
                        </div>
                        <div className='whitespace-nowrap text-right text-sm text-[var(--color-text-secondary)]'>
                          <div className='font-medium text-[var(--color-text-primary)]'>
                            {formatDate(assignment.effective_date)}
                          </div>
                          {assignment.expiration_date && (
                            <div className='mt-1 text-xs'>
                              Expired: {formatDate(assignment.expiration_date)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AssignmentHistory;
export { AssignmentHistory };
